import { computed, effect, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, pipe, switchMap, tap } from 'rxjs';

import { CatalogCache } from '../data/catalog-cache';
import { BranchStore } from './branch-store';
import type { Catalog } from '../data/models/catalog/catalog.model';
import type {
  ModificationGroup,
  ModificationItem,
  Product,
  ProductVariant,
  SelectedModification,
} from '../data/models/catalog/product.model';
import type { Promos } from '../data/models/catalog/promo.model';
import type { DeliveryMode } from '../data/models/order/order.model';
import { promoUnitPrice } from '../shared/utils/promo';
import { readStorage, writeStorage } from '../shared/utils/storage';
import { CART_MAX_LINES, CART_MAX_QTY, STORAGE_KEYS } from './constants';
import { PromoStore } from './promo-store';

/** Товар хранится снимком: корзина рисуется до загрузки каталога, актуализация — задача reconcile(). */
export interface CartLine {
  key: string;
  product: { id: string; name: string; imageUrl: string | null };
  variant: ProductVariant | null;
  modifications: SelectedModification[];
  qty: number;
  /** Цена варианта (или базовая цена товара) + Σ (цена дополнения × его количество). */
  unitPrice: number;
}

const DEFAULT_MODE: DeliveryMode = 'delivery';

/** Потолок длины ОТОБРАЖАЕМЫХ строк из localStorage: чужой мусор не должен раздувать рендер. */
const CART_FIELD_MAX = 200;
/** imageUrl — не отображаемая строка, а адрес: inline SVG data-URI и ссылки CDN длинные. */
const CART_IMAGE_MAX = 4096;
/** Столько дополнений не бывает даже у самого сложного блюда — всё сверх этого битая запись. */
const CART_MAX_MODIFICATIONS = 20;

type StoredCart = {
  lines: Map<string, CartLine>;
  mode: DeliveryMode;
};

type CartState = StoredCart;

/** Ключ дополнения в индексе каталога: id элемента уникален только внутри своей группы. */
export function itemKey(groupId: string, itemId: string): string {
  // Части кодируются: разделитель из чужого id иначе склеил бы соседние элементы в один ключ.
  return `${encodeURIComponent(groupId)}:${encodeURIComponent(itemId)}`;
}

/** Сравнение без локали: `localeCompare` дал бы разный порядок частей ключа на разных движках. */
function compareText(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  return left > right ? 1 : 0;
}

type PickRef = { id: string; groupId?: string; qty: number };

function comparePick(left: PickRef, right: PickRef): number {
  const byGroup = compareText(left.groupId ?? '', right.groupId ?? '');
  return byGroup !== 0 ? byGroup : compareText(left.id, right.id);
}

/** Набор дополнений порядконезависим; части кодируются, чтобы разделитель из id не расщепил ключ. */
export function cartLineKey(
  productId: string,
  variantId: string | null,
  mods: readonly PickRef[],
): string {
  // Количество входит в ключ: «2 сыра» и «1 сыр» — разные строки, а не одна с удвоенной ценой.
  // Группа тоже: id элемента уникален лишь внутри неё, иначе тёзки из разных групп схлопнутся.
  const picked = [...mods]
    .sort(comparePick)
    .flatMap((mod) => [mod.groupId ?? '', mod.id, String(mod.qty)]);
  return [productId, variantId ?? '', ...picked].map(encodeURIComponent).join('|');
}

/** Единица со скидкой филиала × qty: округление за единицу, как считает бэкенд. */
export function lineTotal(line: CartLine, promos: Promos): number {
  return promoUnitPrice(line, promos) * line.qty;
}

export function unitPriceOf(
  basePrice: number,
  variant: ProductVariant | null,
  modifications: readonly SelectedModification[],
): number {
  return modifications.reduce((sum, mod) => sum + mod.price * mod.qty, variant?.price ?? basePrice);
}

function clampQty(qty: number): number {
  const whole = Math.trunc(qty);
  if (!Number.isFinite(whole)) {
    return 1;
  }
  return Math.min(Math.max(whole, 1), CART_MAX_QTY);
}

function snapshotOf(product: Product): CartLine['product'] {
  return { id: product.id, name: product.name, imageUrl: product.imageUrl };
}

// Без trim(): из этих строк считается cartLineKey, нормализация развела бы восстановленные строки.
function clampText(value: string): string {
  return value.slice(0, CART_FIELD_MAX);
}

/** Объект пересобирается из известных полей: лишнее из storage не переживает restore → persist. */
function restoreVariant(entity: ProductVariant): ProductVariant {
  return { id: clampText(entity.id), name: clampText(entity.name), price: entity.price };
}

function restoreModification(entity: SelectedModification): SelectedModification {
  return {
    id: clampText(entity.id),
    groupId: clampText(entity.groupId),
    name: clampText(entity.name),
    price: entity.price,
    qty: entity.qty,
  };
}

function isVariant(value: unknown): value is ProductVariant {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { id, name, price } = value as { id?: unknown; name?: unknown; price?: unknown };
  return (
    typeof id === 'string' &&
    typeof name === 'string' &&
    typeof price === 'number' &&
    Number.isFinite(price) &&
    // Как у unitPrice: отрицательная цена из storage рисовала бы минусовую базу в составе строки.
    price >= 0
  );
}

function isSelectedModification(value: unknown): value is SelectedModification {
  if (!isVariant(value)) {
    return false;
  }
  const { groupId, qty } = value as ProductVariant & { groupId?: unknown; qty?: unknown };
  return (
    typeof groupId === 'string' &&
    // Дробное или нулевое количество ломает и цену, и ключ строки — запись не берём.
    typeof qty === 'number' &&
    Number.isInteger(qty) &&
    qty >= 1 &&
    qty <= CART_MAX_QTY
  );
}

/** JSON из storage может быть чужим, старым или битым — проверяем ТИПЫ, а не наличие полей. */
function isStoredLine(value: unknown): value is CartLine {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { product, variant, modifications, qty, unitPrice } = value as {
    product?: unknown;
    variant?: unknown;
    modifications?: unknown;
    qty?: unknown;
    unitPrice?: unknown;
  };
  if (typeof product !== 'object' || product === null) {
    return false;
  }
  const { id, name, imageUrl } = product as { id?: unknown; name?: unknown; imageUrl?: unknown };
  return (
    typeof id === 'string' &&
    typeof name === 'string' &&
    // URL не обрезаем, а отвергаем запись целиком: обрезанный data-URI просто битый.
    (imageUrl === null || (typeof imageUrl === 'string' && imageUrl.length <= CART_IMAGE_MAX)) &&
    (variant === null || isVariant(variant)) &&
    Array.isArray(modifications) &&
    modifications.length <= CART_MAX_MODIFICATIONS &&
    modifications.every(isSelectedModification) &&
    typeof qty === 'number' &&
    Number.isFinite(qty) &&
    qty >= 1 &&
    typeof unitPrice === 'number' &&
    Number.isFinite(unitPrice) &&
    unitPrice >= 0
  );
}

function isDeliveryMode(value: unknown): value is DeliveryMode {
  return value === 'delivery' || value === 'pickup';
}

function restoreLines(value: unknown): Map<string, CartLine> {
  const lines = new Map<string, CartLine>();
  if (!Array.isArray(value)) {
    return lines;
  }
  for (const raw of value) {
    if (lines.size >= CART_MAX_LINES) {
      break;
    }
    if (!isStoredLine(raw)) {
      continue;
    }
    const product = {
      id: clampText(raw.product.id),
      name: clampText(raw.product.name),
      // imageUrl переносится как есть — длина уже проверена в isStoredLine.
      imageUrl: raw.product.imageUrl,
    };
    const variant = raw.variant === null ? null : restoreVariant(raw.variant);
    const modifications = raw.modifications.map(restoreModification);
    // Ключ пересчитываем: подменённый развёл бы одинаковые позиции по строкам или схлопнул разные.
    const key = cartLineKey(product.id, variant?.id ?? null, modifications);
    const existing = lines.get(key);
    lines.set(key, {
      key,
      product,
      variant,
      modifications,
      qty: clampQty((existing?.qty ?? 0) + raw.qty),
      unitPrice: raw.unitPrice,
    });
  }
  return lines;
}

function emptyCart(): StoredCart {
  return { lines: new Map(), mode: DEFAULT_MODE };
}

function readStoredCart(): StoredCart {
  const raw = readStorage(STORAGE_KEYS.CART);
  if (!raw) {
    return emptyCart();
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyCart();
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return emptyCart();
  }
  const { lines, mode } = parsed as { lines?: unknown; mode?: unknown };
  return {
    lines: restoreLines(lines),
    mode: isDeliveryMode(mode) ? mode : DEFAULT_MODE,
  };
}

function persistCart(lines: CartLine[], mode: DeliveryMode): void {
  const empty = lines.length === 0 && mode === DEFAULT_MODE;
  writeStorage(STORAGE_KEYS.CART, empty ? null : JSON.stringify({ mode, lines }));
}

type CatalogIndex = {
  groups: ReadonlyMap<string, ModificationGroup>;
  items: ReadonlyMap<string, ModificationItem>;
};

/** `null` — без ограничения; мусорный потолок (0 или отрицательный) тоже ничего не режет. */
function overLimit(value: number, max: number | null): boolean {
  return max !== null && max > 0 && value > max;
}

/** Свежие дополнения строки; `null` — элемента, группы или её лимитов строка больше не проходит. */
function freshModifications(
  line: CartLine,
  product: Product,
  index: CatalogIndex,
): SelectedModification[] | null {
  const attached = new Set(product.modificationGroupIds);
  const perGroup = new Map<string, number>();
  const next: SelectedModification[] = [];
  for (const picked of line.modifications) {
    const item = index.items.get(itemKey(picked.groupId, picked.id));
    // Потолок штук на бэкенде мог опуститься: заказ с превышением сервер всё равно отвергнет.
    if (!item || !attached.has(item.groupId) || overLimit(picked.qty, item.maxOrderSelect)) {
      return null;
    }
    const count = (perGroup.get(item.groupId) ?? 0) + 1;
    if (overLimit(count, index.groups.get(item.groupId)?.maxSelect ?? null)) {
      return null;
    }
    perGroup.set(item.groupId, count);
    next.push({
      id: item.id,
      groupId: item.groupId,
      name: item.name,
      price: item.price,
      qty: picked.qty,
    });
  }
  // Группа могла стать обязательной уже после добавления: такой заказ сервер отвергнет целиком.
  for (const groupId of product.modificationGroupIds) {
    if (index.groups.get(groupId)?.required === true && !perGroup.has(groupId)) {
      return null;
    }
  }
  return next;
}

function indexCatalog(catalog: Catalog): CatalogIndex {
  const groups = new Map<string, ModificationGroup>();
  const items = new Map<string, ModificationItem>();
  for (const group of catalog.modificationGroups) {
    groups.set(group.id, group);
    for (const item of group.items) {
      items.set(itemKey(group.id, item.id), item);
    }
  }
  return { groups, items };
}

// zoneless: Map — листовое значение DeepSignal, менять только patchState с НОВЫМ Map.
/** Root: /checkout и /login живут вне shell — страничный стор потерял бы содержимое. */
export const CartStore = signalStore(
  { providedIn: 'root' },
  withState<CartState>(() => readStoredCart()),

  withComputed((state) => ({
    items: computed(() => [...state.lines().values()]),
  })),
  withComputed((state, promo = inject(PromoStore)) => ({
    count: computed(() => state.items().reduce((sum, line) => sum + line.qty, 0)),
    // Со скидкой: одна сумма для подытога, минималки, порога доставки, чекаута и плашки «Оформить».
    total: computed(() => {
      const promos = promo.view();
      return state.items().reduce((sum, line) => sum + lineTotal(line, promos), 0);
    }),
    isEmpty: computed(() => state.lines().size === 0),
  })),

  withMethods((store) => ({
    /** `false` — только когда НОВАЯ строка не влезает в CART_MAX_LINES; упор в потолок qty — успех. */
    add(
      product: Product,
      variant: ProductVariant | null = null,
      modifications: SelectedModification[] = [],
      qty = 1,
    ): boolean {
      const key = cartLineKey(product.id, variant?.id ?? null, modifications);
      const lines = store.lines();
      const existing = lines.get(key);
      if (!existing && lines.size >= CART_MAX_LINES) {
        return false;
      }

      const next = new Map(lines);
      // Повторное добавление идёт из свежего каталога: от прежней строки остаётся только количество.
      next.set(key, {
        key,
        product: snapshotOf(product),
        variant,
        modifications: modifications.map((mod) => ({ ...mod })),
        qty: clampQty((existing?.qty ?? 0) + clampQty(qty)),
        unitPrice: unitPriceOf(product.price, variant, modifications),
      });
      patchState(store, { lines: next });
      return true;
    },

    /** qty <= 0 — строка удаляется (степпер «−» на единице убирает позицию). */
    setQty(key: string, qty: number): void {
      const lines = store.lines();
      const line = lines.get(key);
      if (!line) {
        return;
      }
      const next = new Map(lines);
      const whole = Math.trunc(qty);
      if (!Number.isFinite(whole) || whole <= 0) {
        next.delete(key);
      } else {
        next.set(key, { ...line, qty: clampQty(whole) });
      }
      patchState(store, { lines: next });
    },

    remove(key: string): void {
      const lines = store.lines();
      if (!lines.has(key)) {
        return;
      }
      const next = new Map(lines);
      next.delete(key);
      patchState(store, { lines: next });
    },

    clear(): void {
      if (store.lines().size === 0) {
        return;
      }
      patchState(store, { lines: new Map() });
    },

    reset(): void {
      patchState(store, emptyCart());
    },

    setMode(mode: DeliveryMode): void {
      patchState(store, { mode });
    },

    /** Исчезнувший вариант, группа или элемент убирает строку целиком: тихая подмена = не тот заказ. */
    reconcile(catalog: Catalog): void {
      const byId = new Map(catalog.products.map((product) => [product.id, product]));
      const index = indexCatalog(catalog);
      const current = store.lines();
      const next = new Map<string, CartLine>();
      let changed = false;

      for (const [key, line] of current) {
        const fresh = byId.get(line.product.id);
        if (!fresh) {
          changed = true;
          continue;
        }

        const variant = line.variant
          ? (fresh.variants.find((item) => item.id === line.variant?.id) ?? null)
          : null;
        if (line.variant && !variant) {
          changed = true;
          continue;
        }

        const modifications = freshModifications(line, fresh, index);
        if (!modifications) {
          changed = true;
          continue;
        }

        const unitPrice = unitPriceOf(fresh.price, variant, modifications);
        const same =
          fresh.name === line.product.name &&
          fresh.imageUrl === line.product.imageUrl &&
          unitPrice === line.unitPrice &&
          (variant?.name ?? null) === (line.variant?.name ?? null) &&
          modifications.every((mod, index) => mod.name === line.modifications[index]?.name);

        next.set(
          key,
          same ? line : { ...line, product: snapshotOf(fresh), variant, modifications, unitPrice },
        );
        if (!same) {
          changed = true;
        }
      }

      // Без изменений — ничего не пишем: новый экземпляр Map перерисовал бы всю корзину.
      if (changed) {
        patchState(store, { lines: next });
      }
    },
  })),

  withMethods((store, catalog = inject(CatalogCache)) => ({
    /** Без гейта «раз за сессию»: catalog$ кэширован по языку, а живая подписка
     * даёт корзине новые имена при смене языка (reconcile идемпотентен и дёшев). */
    ensureReconciled: rxMethod<void>(
      pipe(
        switchMap(() =>
          catalog.catalog$.pipe(
            tap((fresh) => store.reconcile(fresh)),
            // catchError во ВНУТРЕННЕМ pipe: подписка переживает ошибку каталога.
            catchError(() => EMPTY),
          ),
        ),
      ),
    ),
  })),

  withHooks({
    onInit(store, branch = inject(BranchStore)) {
      // Одностороннее зеркало: состояние -> storage, обратно только при старте.
      effect(() => {
        persistCart(store.items(), store.mode());
      });
      // Самовывоз без филиалов невозможен: сохранённый режим не должен пережить пустой список.
      effect(() => {
        if (store.mode() === 'pickup' && !branch.pickupAvailable()) {
          patchState(store, { mode: DEFAULT_MODE });
        }
      });
    },
  }),
);
